import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    CircuitBoard,
    SunMedium,
    Music2,
    ShieldCheck,
    Network,
    Building2,
    ArrowUpRight,
} from "lucide-react";
import SectionLabel from "../components/SectionLabel";
import SEO from "../components/SEO";
import CTABand from "../components/sections/CTABand";
import { SERVICES } from "../data/content";

const ICONS = {
    CircuitBoard,
    SunMedium,
    Music2,
    ShieldCheck,
    Network,
    Building2,
};

export default function ServicesIndex() {
    return (
        <div data-testid="services-index-page">
            <SEO
                title="Smart Home Services — Automation, Lighting, Cinema, Security"
                description="Home automation, lighting, media rooms, security, networking, and commercial AV — engineered as one system for luxury residences."
                canonical="https://actluxe.net/services"
            />
            {/* Page header */}
            <section className="relative bg-[#0B0B0C] pt-20 md:pt-28 pb-16 md:pb-24 border-b border-[rgba(201,168,106,0.12)]">
                <div className="shell">
                    <SectionLabel number="Services" label="Six Disciplines" />
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mt-8 items-end">
                        <h1 className="lg:col-span-7 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-3xl">
                            The full measure of{" "}
                            <span className="italic text-[#C9A86A]">
                                an ACT residence.
                            </span>
                        </h1>
                        <p className="lg:col-span-5 text-[#9A958A] text-base md:text-lg leading-[1.85] max-w-md lg:pb-2">
                            Every discipline below is authored, installed, and
                            maintained in-house. Begin with a single service,
                            or let us compose the entire home.
                        </p>
                    </div>
                </div>
            </section>

            {/* Editorial service list */}
            <section className="bg-[#0B0B0C]">
                <div className="shell">
                    {SERVICES.map((s, idx) => {
                        const Icon = ICONS[s.icon] ?? CircuitBoard;
                        return (
                            <motion.article
                                key={s.slug}
                                suppressHydrationWarning
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{
                                    duration: 0.8,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="group relative border-b border-[rgba(201,168,106,0.15)] last:border-b-0"
                                data-testid={`services-index-item-${s.slug}`}
                            >
                                <Link
                                    to={`/services/${s.slug}`}
                                    className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 py-14 md:py-20 transition-colors duration-500 ease-out"
                                >
                                    <div className="lg:col-span-1 flex lg:justify-start items-start">
                                        <span className="eyebrow">
                                            {s.number}
                                        </span>
                                    </div>

                                    <div className="lg:col-span-6">
                                        <div className="flex items-center gap-5 mb-3">
                                            <Icon
                                                size={22}
                                                strokeWidth={1.1}
                                                className="text-[#C9A86A]"
                                            />
                                            <span className="text-[11px] tracking-[0.32em] uppercase text-[#9A958A]">
                                                {s.tagline}
                                            </span>
                                        </div>
                                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#F3EFE7] leading-[1.08] transition-colors duration-500 group-hover:text-[#D9BC80]">
                                            {s.name}
                                        </h2>
                                    </div>

                                    <div className="lg:col-span-4 lg:pl-4 flex flex-col gap-6">
                                        <p className="text-[#9A958A] text-base md:text-[17px] leading-[1.85]">
                                            {s.short}
                                        </p>
                                    </div>

                                    <div className="lg:col-span-1 flex lg:justify-end items-start">
                                        <span className="inline-flex items-center justify-center w-12 h-12 border border-[#C9A86A]/50 text-[#C9A86A] transition-all duration-500 ease-out group-hover:bg-[#C9A86A] group-hover:text-[#0B0B0C]">
                                            <ArrowUpRight
                                                size={18}
                                                strokeWidth={1.1}
                                            />
                                        </span>
                                    </div>
                                </Link>
                                {idx === 0 && (
                                    <span
                                        aria-hidden="true"
                                        className="absolute top-0 left-0 right-0 hairline-solid"
                                    />
                                )}
                            </motion.article>
                        );
                    })}
                </div>
            </section>

            <CTABand />
        </div>
    );
}
